// useFocusTrap — focus management for aria-modal dialogs (WCAG 2.4.3 / 2.1.2).
//
// Usage:
//   const dialogRef = useRef<HTMLDivElement>(null);
//   useFocusTrap(dialogRef, open, { onEscape: onClose });
//   <div ref={dialogRef} role="dialog" aria-modal="true">…</div>
//
// While open: Tab / Shift-Tab cycle inside the container; initial focus
// moves to `initialFocus` if provided, else the first focusable element,
// else the container itself (given tabindex="-1" so it can hold focus).
// If `onEscape` is provided, Escape calls it — pass it for dialogs that
// don't already wire their own Escape handler. On close/unmount, focus
// returns to the element that was focused before the dialog opened.

import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

type Options = {
  initialFocus?: RefObject<HTMLElement | null>;
  onEscape?: () => void;
};

export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  isOpen: boolean,
  options: Options = {},
) {
  // Options live in a ref so an inline `onEscape` arrow doesn't re-run the
  // effect (and re-steal focus) on every parent render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!isOpen) return;
    const node = ref.current;
    if (!node) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const focusables = () =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.getClientRects().length > 0,
      );

    const initial = optionsRef.current.initialFocus?.current ?? focusables()[0];
    if (initial) {
      initial.focus();
    } else {
      // Dialog with no focusable children (e.g. an image lightbox): the
      // container itself holds focus so Escape and Tab are catchable.
      if (!node.hasAttribute("tabindex")) node.setAttribute("tabindex", "-1");
      node.focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        optionsRef.current.onEscape?.();
        return;
      }
      if (e.key !== "Tab") return;
      const list = focusables();
      if (list.length === 0) {
        e.preventDefault();
        node.focus();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      const inside = active instanceof HTMLElement && node.contains(active);
      if (e.shiftKey) {
        if (!inside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      previouslyFocused?.focus();
    };
  }, [ref, isOpen]);
}
